import styled, { keyframes } from 'styled-components';

const pop = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  70%  { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const ConfirmedContainer = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 20px 72px;

  > * { animation: ${fadeUp} 0.5s ease both; }
  > *:nth-child(2) { animation-delay: 0.08s; }
  > *:nth-child(3) { animation-delay: 0.16s; }
  > *:nth-child(4) { animation-delay: 0.24s; }

  @media (prefers-reduced-motion: reduce) {
    > * { animation: none; }
  }
`;

export const SuccessHeader = styled.div`
  text-align: center;
  margin-bottom: 36px;

  h1 {
    font-size: var(--text-3xl);
    margin-bottom: 8px;
  }

  p {
    color: var(--text-secondary);
    font-size: var(--text-base);
  }
`;

export const SuccessIcon = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: var(--success);
  color: var(--primary);
  font-size: 36px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-success);
  animation: ${pop} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const QRSection = styled.section`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 32px;
  text-align: center;
  margin-bottom: 24px;
  box-shadow: var(--shadow-sm);

  h2 {
    font-size: var(--text-xl);
    margin-bottom: 4px;
  }

  > p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    margin-bottom: 20px;
  }
`;

export const ReservationCard = styled.section`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 28px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-sm);

  h2 {
    font-size: var(--text-xl);
    margin-bottom: 18px;
  }
`;

export const ReservationDetails = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 8px 0;
    font-size: var(--text-sm);
    border-bottom: 1px solid var(--border-light);

    span:first-child { color: var(--text-secondary); flex-shrink: 0; }
    span:last-child { font-weight: 600; text-align: right; }

    .mono {
      font-family: monospace;
      font-size: var(--text-xs);
      word-break: break-all;
    }

    &.total {
      border-bottom: none;
      padding-top: 12px;

      span:last-child {
        color: var(--secondary);
        font-size: var(--text-lg);
        font-weight: 700;
      }
    }
  }

  .customer-info {
    background: var(--bg-section);
    border-radius: var(--radius-md);
    padding: 16px;
    align-self: start;

    h3 {
      font-size: var(--text-sm);
      font-weight: 600;
      margin-bottom: 10px;
      color: var(--text-secondary);
      font-family: var(--font-brand);
    }

    p {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      margin: 3px 0;
      word-break: break-word;
    }
  }
`;

export const ImportantNotes = styled.section`
  background: var(--primary-subtle);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 24px 28px;
  margin-bottom: 24px;

  h3 {
    font-size: var(--text-base);
    color: var(--primary-dark);
    margin-bottom: 12px;
    font-family: var(--font-brand);
    font-weight: 700;
  }

  ul {
    list-style: none;

    li {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      padding: 5px 0 5px 22px;
      position: relative;

      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: var(--primary);
        font-weight: 700;
      }

      strong { color: var(--text-primary); }
    }
  }
`;

export const ShareSection = styled.section`
  text-align: center;
  margin-bottom: 32px;

  h3 {
    font-size: var(--text-base);
    margin-bottom: 12px;
    font-family: var(--font-brand);
    font-weight: 600;
    color: var(--text-secondary);
  }

  .share-buttons {
    display: flex;
    gap: 10px;
    justify-content: center;

    button {
      padding: 10px 24px;
      border-radius: var(--radius-round);
      border: 1.5px solid var(--border-medium);
      background: var(--bg-card);
      color: var(--text-primary);
      font-family: var(--font-brand);
      font-size: var(--text-sm);
      font-weight: 600;
      cursor: pointer;
      transition: all var(--transition-fast);

      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-sm);
      }

      &.whatsapp {
        background: var(--success);
        border-color: var(--success);
        color: var(--primary);

        &:hover { box-shadow: var(--shadow-success); }
      }
    }
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;
